import React, { useState, useEffect, useMemo } from 'react';
import { Dialog } from '../dialogs/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { TabView, TabPanel } from 'primereact/tabview';
import { Button } from 'primereact/button';
import type { GraphMetadata } from '../content-editor/tiptap-graph-node-view';
import { useFullscreenDialogParent } from '../../general/use-fullscreen-dialog-parent';
import { emptyGraphMetadata, GraphData, GRAPH_MIN_HEIGHT, GRAPH_MAX_HEIGHT, readGraphHeight, parseGraph, serializeGraph, validateGraphForSave } from './graph-model';
import { GraphVisualBuilder } from './graph-visual-builder';
import './graph-dialog.css';

export interface GraphDialogProps {
    visible: boolean;
    onHide: () => void;
    onSave: (data: GraphMetadata) => void;
    initialData: GraphMetadata | null;
}

const jsonParts = [
    { key: 'nodesJson', label: 'Nodes JSON' },
    { key: 'edgesJson', label: 'Edges JSON' },
    { key: 'examplesJson', label: 'Examples JSON' },
] as const;

export const GraphDialog: React.FC<GraphDialogProps> = ({ visible, onHide, onSave, initialData }) => {
    const [metadata, setMetadata] = useState<GraphMetadata>(initialData ?? emptyGraphMetadata);
    const [mode, setMode] = useState(0);
    const [jsonTab, setJsonTab] = useState(0);
    const [actionError, setActionError] = useState('');
    const parent = useFullscreenDialogParent();
    useEffect(() => {
        if (!visible) return;
        const initial = initialData ?? emptyGraphMetadata;
        setMetadata(initial); setActionError(''); setJsonTab(0);
        try { parseGraph(initial); setMode(0); }
        catch { setMode(1); }
    }, [visible, initialData]);

    const parsed = useMemo(() => {
        try { return { graph: parseGraph(metadata), error: '' }; }
        catch (error) { return { graph: null, error: (error as Error).message }; }
    }, [metadata]);
    const changeGraph = (graph: GraphData): boolean => {
        const next = serializeGraph(graph);
        try { parseGraph(next); }
        catch (error) { setActionError((error as Error).message); return false; }
        setMetadata(current => ({ ...current, ...next })); setActionError(''); return true;
    };
    const save = () => {
        try {
            const graph = parseGraph(metadata);
            validateGraphForSave(graph);
            const heightText = String(metadata.height ?? '').trim();
            if (heightText && heightText.toLowerCase() !== 'auto' && readGraphHeight(metadata.height) === null) {
                throw new Error(`Enter a positive height with px, %, vh, vw, em or rem. Pixel heights must be between ${GRAPH_MIN_HEIGHT} and ${GRAPH_MAX_HEIGHT}; leave empty for automatic sizing.`);
            }
            // Preserve raw formatting and every unexposed property on save.
            onSave({ ...metadata, ...(metadata.height !== undefined ? { height: readGraphHeight(metadata.height) } : {}) });
            onHide();
        } catch (error) { setActionError((error as Error).message); }
    };
    const formatJson = () => {
        const part = jsonParts[jsonTab];
        try {
            const formatted = JSON.stringify(JSON.parse(metadata[part.key]), null, 2);
            setMetadata(current => ({ ...current, [part.key]: formatted })); setActionError('');
        } catch { setActionError(`${part.label}: cannot format invalid JSON. Your text has been retained.`); }
    };
    const error = actionError || parsed.error;

    return <Dialog header="Graph" visible={visible} onHide={onHide} modal appendTo={parent}
        className="graph-dialog" style={{ width: 'min(76rem, 96vw)', height: '90vh', maxHeight: '95vh' }} draggable={false}
        footer={<div><Button label="Cancel" icon="pi pi-times" text onClick={onHide} />
            <Button label="Save graph" icon="pi pi-check" onClick={save} /></div>}>
        <div className="graph-display-settings">
            <label htmlFor="graph-display-height">Graph height</label>
            <InputText id="graph-display-height" value={String(metadata.height ?? '')} placeholder="Auto"
                aria-describedby="graph-height-help"
                onChange={event => { setMetadata(current => ({ ...current, height: event.target.value })); setActionError(''); }} />
            <small id="graph-height-help">Examples: 600, 600px, 50%, 50vh, 30rem. Numbers use px; percentages use the visible lesson/editor height. Leave empty for automatic sizing.</small>
        </div>
        <TabView className="graph-dialog-tabs" activeIndex={mode} onTabChange={event => { setMode(event.index); setActionError(''); }}>
            <TabPanel header="Visual builder">
                {parsed.graph ? <GraphVisualBuilder graph={parsed.graph} onChange={changeGraph} /> :
                    <div><p>The graph cannot be displayed until its JSON is corrected. Your text has been retained.</p>
                        <Button label="Edit JSON" icon="pi pi-code" outlined onClick={() => setMode(1)} /></div>}
            </TabPanel>
            <TabPanel header="JSON">
                <p className="text-color-secondary mt-0">Edit the graph data directly. Existing x, y and additional properties are preserved when using the visual builder; positioning remains automatic.</p>
                <TabView activeIndex={jsonTab} onTabChange={event => { setJsonTab(event.index); setActionError(''); }}>
                    {jsonParts.map(part => <TabPanel key={part.key} header={part.label}>
                        <InputTextarea aria-label={part.label} value={metadata[part.key]} spellCheck={false} className="graph-json-editor"
                            onChange={event => { setMetadata(current => ({ ...current, [part.key]: event.target.value })); setActionError(''); }} />
                    </TabPanel>)}
                </TabView>
                <Button label="Format JSON" icon="pi pi-align-left" outlined onClick={formatJson} />
            </TabPanel>
        </TabView>
        {error && <div className="graph-dialog-error" role="alert"><i className="pi pi-exclamation-triangle" aria-hidden="true" /><span>{error}</span></div>}
    </Dialog>;
};
